import type { AreaGuideSpotCategory } from "../../../../../../__generated__/gql/graphql";

/**
 * タブとして表示するカテゴリ (表示順)。 `slug` は公開 URL の `?category=` に使う値で、
 * 一度公開したら変更しない (検索 URL のリンク切れ防止)。
 * `AreaGuideSpotCategory` の `Others` (その他) はタブには出さない。
 */
export const SPOT_CATEGORY_TABS = [
  { slug: "food-market", category: "FoodStore", label: "GROCERY" },
  { slug: "restaurant", category: "CafeRestaurant", label: "DINING" },
  { slug: "shop", category: "Shop", label: "SHOP" },
  { slug: "nature", category: "NatureSpot", label: "NATURE" },
  { slug: "event", category: "Events", label: "EVENT" },
] as const satisfies ReadonlyArray<{
  slug: string;
  category: AreaGuideSpotCategory;
  label: string;
}>;

/**
 * 全カテゴリのラベル (その他を含む)。 カード内のカテゴリ行表示に使う。
 * `Record<AreaGuideSpotCategory, string>` なので enum 追加時に型エラーで気付ける。
 */
export const SPOT_CATEGORY_LABELS: Record<AreaGuideSpotCategory, string> = {
  FoodStore: "GROCERY",
  CafeRestaurant: "DINING",
  Shop: "SHOP",
  NatureSpot: "NATURE",
  Events: "EVENT",
  Others: "OTHERS",
};

/** category slug を enum に変換。 null / 未知の slug は null (= 絞り込み無し扱い)。 */
export function categorySlugToCategory(
  slug: string | null | undefined,
): AreaGuideSpotCategory | null {
  if (slug == null) return null;
  return SPOT_CATEGORY_TABS.find((tab) => tab.slug === slug)?.category ?? null;
}

/**
 * spot の categories から絞り込み用 URL slug を 1 つ選ぶ。
 * categories の並び順を尊重し、最初にタブ対象 (food-market/restaurant/shop/nature/event) に
 * 該当するカテゴリの slug を返す。タブ対象が無い (Others のみ等) なら null = 絞り込み無し。
 * エリアページの spot カードから「そのカテゴリで絞った一覧」へ遷移するリンク生成に使う。
 */
export function spotCategorySlug(
  categories: ReadonlyArray<AreaGuideSpotCategory>,
): string | null {
  return (
    categories
      .map((c) => SPOT_CATEGORY_TABS.find((tab) => tab.category === c)?.slug)
      .find((slug) => slug != null) ?? null
  );
}

/**
 * カードのカテゴリ行ラベル。 大カテゴリとタグを "・" で連結する。
 * 例: categories=[FoodStore], tags=["スーパー"] → "GROCERY・スーパー"
 */
export function buildSpotCategoryLabel(
  categories: ReadonlyArray<AreaGuideSpotCategory>,
  tags: ReadonlyArray<string>,
): string {
  return [...categories.map((c) => SPOT_CATEGORY_LABELS[c]), ...tags].join(
    "・",
  );
}
